(() => {
  'use strict';

  if (window.__pwBlogAttributionInitialized) return;
  window.__pwBlogAttributionInitialized = true;

  const STORAGE_KEY = 'pw_blog_attribution_v1';
  const SESSION_KEY = 'pw_blog_attribution_events_v1';
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  const MAX_VALUE_LENGTH = 180;
  let syncPromise = null;

  function cleanValue(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_VALUE_LENGTH);
  }

  function currentRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function analyticsAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;

    if (privacy && typeof privacy.currentVisitorConsent === 'function') {
      try {
        const consent = privacy.currentVisitorConsent();
        if (consent && consent.analytics === 'no') return false;
      } catch (error) {
        // Keep the existing storefront analytics behaviour if the privacy API is unavailable.
      }
    }

    return true;
  }

  function readStorage() {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      if (!value) return null;

      const parsed = JSON.parse(value);
      if (!parsed || parsed.version !== 1) return null;

      const newest = parsed.last && parsed.last.seen_at ? parsed.last.seen_at : parsed.first && parsed.first.seen_at;
      if (newest && Date.now() - new Date(newest).getTime() > MAX_AGE_MS) return null;

      return parsed;
    } catch (error) {
      return null;
    }
  }

  function writeStorage(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      // Attribution must never block navigation or checkout.
    }
  }

  function articleRecord(context) {
    if (!context || !context.handle) return null;

    return {
      handle: cleanValue(context.handle),
      title: cleanValue(context.title),
      url: cleanValue(context.url),
      locale: cleanValue(context.locale || document.documentElement.lang),
      seen_at: new Date().toISOString()
    };
  }

  function saveArticleTouch(context) {
    const article = articleRecord(context);
    if (!article) return readStorage();

    const previous = readStorage();
    const next = {
      version: 1,
      first: previous && previous.first ? previous.first : article,
      last: article
    };

    writeStorage(next);
    return next;
  }

  function rememberEvent(article) {
    try {
      const key = article.handle + ':' + article.url;
      const events = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || '[]');
      if (events.indexOf(key) !== -1) return false;

      events.push(key);
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(events.slice(-20)));
      return true;
    } catch (error) {
      return true;
    }
  }

  function sendAnalytics(article, record) {
    if (!analyticsAllowed() || !article) return;

    const first = record && record.first ? record.first : article;
    const parameters = {
      pw_blog_article_handle: article.handle,
      pw_blog_article_title: article.title,
      pw_blog_touch_model: article.handle === first.handle ? 'first_touch' : 'last_touch',
      pw_blog_article_url: article.url,
      pw_blog_article_locale: article.locale,
      pw_blog_attribution_version: '1'
    };

    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      if (rememberEvent(article)) {
        window.gtag('event', 'blog_attribution', parameters);
      }
      window.gtag('set', 'user_properties', {
        pw_blog_last_article: record && record.last ? record.last.handle : article.handle,
        pw_blog_first_article: first.handle
      });
    } else if (rememberEvent(article)) {
      window.dataLayer.push({ event: 'blog_attribution', ...parameters });
    }
  }

  function cartAttributes(record) {
    if (!record) return {};

    const attributes = {
      pw_blog_attribution_version: '1',
      pw_blog_attribution_model: 'first_and_last_touch'
    };

    if (record.first) {
      attributes.pw_blog_first_article = cleanValue(record.first.handle);
      attributes.pw_blog_first_url = cleanValue(record.first.url);
      attributes.pw_blog_first_seen = cleanValue(record.first.seen_at);
    }

    if (record.last) {
      attributes.pw_blog_last_article = cleanValue(record.last.handle);
      attributes.pw_blog_last_url = cleanValue(record.last.url);
      attributes.pw_blog_last_seen = cleanValue(record.last.seen_at);
    }

    return attributes;
  }

  async function syncCart(record) {
    if (!analyticsAllowed() || !record || syncPromise) return syncPromise;

    syncPromise = (async () => {
      const root = currentRoot();
      const cartResponse = await fetch(root + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const attributes = Object.assign({}, cart.attributes || {}, cartAttributes(record));
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ attributes })
      });
    })().catch(() => {
      // A tracking failure must never interrupt the shopping flow.
    }).finally(() => {
      syncPromise = null;
    });

    return syncPromise;
  }

  function waitForCartSync(record, callback) {
    let finished = false;
    const complete = () => {
      if (finished) return;
      finished = true;
      callback();
    };

    Promise.resolve(syncCart(record)).then(complete, complete);
    window.setTimeout(complete, 900);
  }

  const context = window.PW_BLOG_CONTEXT;
  const record = context ? saveArticleTouch(context) : readStorage();

  if (context) {
    sendAnalytics(articleRecord(context), record);
  }

  if (record) {
    syncCart(record);
  }

  document.addEventListener('cart:change', () => syncCart(readStorage()));
  document.addEventListener('cart:updated', () => syncCart(readStorage()));

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.pwAttributionSubmitting === '1') return;

    const action = (form.getAttribute('action') || '').toLowerCase();
    const submitter = event.submitter;
    const isCartForm = action.indexOf('/cart') !== -1 && action.indexOf('/cart/add') === -1;
    const isCheckout = submitter && (submitter.name === 'checkout' || submitter.value === 'Checkout');
    if (!isCartForm || !isCheckout || !record) return;

    event.preventDefault();
    form.dataset.pwAttributionSubmitting = '1';
    waitForCartSync(record, () => {
      if (typeof form.requestSubmit === 'function' && submitter) {
        form.requestSubmit(submitter);
      } else {
        form.submit();
      }
    });
  }, true);
})();

